import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  ChevronRight,
  Scissors,
  ShoppingBag,
  Sun,
  Layers,
} from "lucide-react";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { ReportDateRangePicker } from "../../components/reports/ReportDateRangePicker";
import { startOfMonth, subDays, format } from "date-fns";
import { useAuth } from "../../auth/AuthContext";

// --- Mock Data & Constants ---

const STAFF_COLORS = ["#885ABB", "#5AB7BB", "#C96EBE", "#837EED"];
const REVENUE_COLORS = ["#885ABB", "#A581CE", "#C2A8E1", "#DFCFF4"];
const STATUS_COLORS = ["#885ABB", "#A581CE", "#C2A8E1", "#DFCFF4"];

const generateRevenueData = () => {
  return Array.from({ length: 14 }).map((_, i) => ({
    name: format(subDays(new Date(), 13 - i), "d MMM"),
    revenue: Math.floor(Math.random() * 2000) + 1000,
    sales: Math.floor(Math.random() * 50) + 10,
  }));
};


const revenueByChannelData = [
  { name: "Services", value: 4500 },
  { name: "Products", value: 1500 },
  { name: "Sunbeds", value: 850 },
  { name: "Packages", value: 1200 },
];

const staffPerformanceData = [
  { name: "Malik Griffith", revenue: 2400, role: "Senior Stylist" },
  { name: "Muhammad Ishaq", revenue: 1800, role: "Stylist" },
  { name: "Jenny", revenue: 3200, role: "Colorist" },
  { name: "John Doe", revenue: 2100, role: "Junior Stylist" },
];

// --- Components ---

const StatCard = ({
  title,
  value,
  trend,
  trendValue,
}: {
  title: string;
  value: string;
  trend?: "up" | "down";
  trendValue?: string;
}) => (
  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between h-full">
    <div className="flex items-center gap-2 mb-2">
      <h3 className="text-gray-900 font-bold text-base">{title}</h3>
      <Info className="text-gray-400 w-3.5 h-3.5" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900 mb-2">{value}</div>
      {trend && (
        <div className="flex items-center text-xs font-medium">
          <span
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${trend === "up"
              ? "bg-green-50 text-green-700"
              : "bg-red-50 text-red-700"
              }`}
          >
            {trend === "up" ? (
              <ArrowUpRight size={12} />
            ) : (
              <ArrowDownRight size={12} />
            )}
            {trendValue}
          </span>
          <span className="text-gray-500 ml-2">vs comp period</span>
        </div>
      )}
    </div>
  </div>
);

export default function ReportsDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: startOfMonth(new Date()),
    end: new Date(),
  });

  const revenueByCategories = [
    { label: "Services", value: `${currencySymbol}4,500.00` },
    { label: "Service add-ons", value: `${currencySymbol}1,200.00` },
    { label: "Products", value: `${currencySymbol}1,500.00` },
    { label: "Packages", value: `${currencySymbol}850.00` },
    { label: "Memberships", value: `${currencySymbol}0.00` },
  ];

  return (
    <div className="flex-1 flex flex-col space-y-4 lg:space-y-6 overflow-y-auto p-4 lg:p-6 bg-gray-50/50">
      <SubPageHeader
        title="Reports Dashboard"
        subtitle="Real-time analytics and business insights"
        icon={Activity}
        actions={
          <div className="flex items-center gap-3">
            <ReportDateRangePicker
              startDate={dateRange.start}
              endDate={dateRange.end}
              onChange={(start, end) => setDateRange({ start, end })}
            />
          </div>
        }
      />

      {/* Top Section: Sales Summary & Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Total Sales Breakdown Card */}
        <div className="bg-white p-4 lg:p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col h-full">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-2">
              <h3 className="text-gray-900 font-bold text-base lg:text-lg">Total Sales</h3>
              <Info className="text-gray-400 w-3.5 h-3.5 lg:w-4 lg:h-4 cursor-help" />
            </div>
            <button
              onClick={() => navigate("/reports/sales")}
              className="text-xs lg:text-sm font-semibold text-[#885ABB] hover:underline flex items-center"
            >
              View report
            </button>
          </div>

          <div className="mb-6 lg:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl lg:text-3xl font-extrabold text-gray-900">
                {currencySymbol}8,050.00
              </span>
            </div>
            <div className="flex items-center text-[10px] lg:text-sm font-medium">
              <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                <ArrowUpRight size={12} /> 0%
              </span>
              <span className="text-gray-500 ml-2">vs comp period</span>
            </div>
          </div>

          <div className="space-y-2 lg:space-y-3 flex-1">
            {revenueByCategories.map((item, idx) => (
              <div
                key={idx}
                className="flex justify-between items-center text-xs lg:text-sm group cursor-pointer hover:bg-gray-50 p-1.5 -mx-1.5 rounded transition-colors"
              >
                <span className="text-gray-600 font-medium">{item.label}</span>
                <span className="text-gray-900 font-bold">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Total Sales Over Time Chart */}
        <div className="lg:col-span-2 bg-white p-4 lg:p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col h-full">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <h3 className="text-gray-900 font-bold text-base lg:text-lg">
                Total sales over time
              </h3>
              <Info className="text-gray-400 w-3.5 h-3.5 lg:w-4 lg:h-4 cursor-help" />
            </div>
            <button
              onClick={() => navigate("/reports/sales")}
              className="text-xs lg:text-sm font-semibold text-[#885ABB] hover:underline"
            >
              View report
            </button>
          </div>

          <div className="flex-1 w-full min-h-[250px] lg:min-h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={generateRevenueData()}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={true}
                  horizontal={true}
                  stroke="#E5E7EB"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#6B7280", fontSize: 10 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#6B7280", fontSize: 10 }}
                  tickFormatter={(value) => `${currencySymbol}${value}`}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1F2937",
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    color: "#F9FAFB",
                    fontSize: "12px",
                  }}
                  itemStyle={{ color: "#F9FAFB" }}
                  cursor={{ stroke: "#9CA3AF", strokeDasharray: "3 3" }}
                  formatter={(value: any) => [`${currencySymbol}${value ?? 0}`, "Sales"]}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#885ABB"
                  strokeWidth={2}
                  dot={{
                    r: 3,
                    fill: "#885ABB",
                    stroke: "#fff",
                    strokeWidth: 2,
                  }}
                  activeDot={{ r: 5, fill: "#885ABB", stroke: "#fff" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 flex items-center justify-center gap-4 lg:gap-6 text-[10px] lg:text-xs text-gray-500 font-medium">
            <div className="flex items-center gap-1.5 lg:gap-2">
              <div className="w-2 lg:w-2.5 h-2 lg:h-2.5 rounded-full bg-[#885ABB]"></div>
              <span>Selected Period</span>
            </div>
          </div>
        </div>
      </div>

      {/* Middle Section: Small Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          title="Avg sale value"
          value={`${currencySymbol}45.20`}
          trend="up"
          trendValue="2.1%"
        />
        <StatCard title="Online sales" value={`${currencySymbol}3,250`} trend="up" trendValue="5.4%" />
        <StatCard title="Appointments" value="156" trend="down" trendValue="1.2%" />
        <StatCard
          title="Returning rate"
          value="68%"
          trend="up"
          trendValue="3.5%"
        />
      </div>

      {/* Bottom Section: Channel & Staff */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Sales by Channel */}
        <div className="bg-white p-4 lg:p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <h3 className="text-gray-900 font-bold text-base lg:text-lg">
                Sales by channel
              </h3>
              <Info className="text-gray-400 w-3.5 h-3.5 lg:w-4 lg:h-4 cursor-help" />
            </div>
            <button
              onClick={() => navigate("/reports/sales")}
              className="text-xs lg:text-sm font-semibold text-[#885ABB] hover:underline"
            >
              View report
            </button>
          </div>

          <div className="flex flex-col h-full pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {revenueByChannelData.map((item, index) => {
                const total = revenueByChannelData.reduce((acc, curr) => acc + curr.value, 0);
                const percentage = Math.round((item.value / total) * 100);

                const getIcon = (name: string) => {
                  switch (name) {
                    case "Services": return Scissors;
                    case "Products": return ShoppingBag;
                    case "Sunbeds": return Sun;
                    case "Packages": return Layers;
                    default: return Activity;
                  }
                };

                const Icon = getIcon(item.name);

                return (
                  <div
                    key={index}
                    className="bg-gray-50/50 rounded-xl p-4 border border-gray-100 flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2 rounded-lg bg-white shadow-sm border border-gray-100">
                        <Icon size={18} className="text-[#885ABB]" />
                      </div>
                      <span className="text-[10px] lg:text-xs font-bold text-[#885ABB] bg-purple-50 px-2 py-0.5 rounded-full">{percentage}%</span>
                    </div>

                    <div>
                      <div className="text-xl lg:text-2xl font-extrabold text-gray-900 mb-0.5">{currencySymbol}{item.value.toLocaleString()}</div>
                      <div className="text-[10px] lg:text-xs font-semibold text-gray-500 uppercase tracking-wider">{item.name}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Staff Performance */}
        <div className="bg-white p-4 lg:p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <h3 className="text-gray-900 font-bold text-base lg:text-lg">
                Staff Performance
              </h3>
              <Info className="text-gray-400 w-3.5 h-3.5 lg:w-4 lg:h-4 cursor-help" />
            </div>
            <button
              onClick={() => navigate("/reports/sales")}
              className="text-xs lg:text-sm font-semibold text-[#885ABB] hover:underline"
            >
              View report
            </button>
          </div>

          <div className="flex-1 overflow-auto pr-1 scrollbar-hide">
            <div className="space-y-1">
              {staffPerformanceData.map((staff, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-all group cursor-default"
                >
                  <div className="flex items-center gap-3 lg:gap-4">
                    <div
                      className="w-9 h-9 lg:w-10 lg:h-10 rounded-full flex items-center justify-center text-xs lg:text-sm font-bold text-white shadow-sm"
                      style={{
                        backgroundColor:
                          STAFF_COLORS[index % STAFF_COLORS.length],
                      }}
                    >
                      {staff.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-900 group-hover:text-[#885ABB] transition-colors">
                        {staff.name}
                      </div>
                      <div className="text-[10px] lg:text-xs text-gray-500 font-medium">
                        {staff.role}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900">
                      {currencySymbol}{staff.revenue.toLocaleString()}
                    </div>
                    {index === 0 && (
                      <span className="inline-flex items-center text-[9px] lg:text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap">
                        Top Performer
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center">
            <button className="text-xs font-semibold text-gray-500 flex items-center hover:text-[#885ABB] transition-colors gap-1">
              View all staff <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
